/**
 * Panel URL + hydration helpers — the shell-agnostic half of panel state.
 *
 * Serializing the open-panel list into `?panels=`, deciding which shared
 * props (cameraId, t) may ride the URL, hydrating persisted ids back into
 * the `{id, props, config}` records the resolver and allocator expect, and
 * scheduling the flush-synchronous navigation — none of that depends on how
 * a shell arranges its chrome, so every shell shares one implementation.
 *
 * What stays in the shell's atoms (src/state/ui/): how `mode` is decided,
 * what the persisted schema looks like, and the open/close policy.
 */

import { flushSync } from 'react-dom';
import type { NavigateOptions } from 'react-router-dom';

import { authSession } from '@/auth/authSession';
import { isPlainObject } from '@/lib/webStorage';
import type { OpenPanel, PanelId, PanelLocalProps, SharedPanelProps } from '@/types/panels';

import { navigationGestures } from './navigationGestures';
import { isPanelId } from './panelIds';
import { getPanelEntry, panels as registry } from './registry';
import { hydrate } from './resolveOpen';

/** What a navigation names: the path and search the router should go to. */
export interface PanelTarget {
    pathname: string;
    search?: string;
}

// Keys the URL is allowed to carry as panel props. Everything else lives in
// local props in localStorage only — one viewer's choices are not part of a shared link.
export const URL_PROP_KEYS = ['cameraId', 't', 'layoutId', 'settingsTab'];

/**
 * Strip the org-scoped props out of a `path?search` target, keeping `panels=`
 * and anything else. Used when a login or an org switch lands on a URL captured
 * under a DIFFERENT organization (AuthGate's `?next=`): the panel set is still
 * the right workspace, but `cameraId` / `t` name an entity the incoming org has
 * never heard of and would open the player on nothing.
 */
export function stripOrgScopedParams(target: string): string {
    const [path = '', search = ''] = target.split('?');
    if (!search) {
        return path;
    }
    const params = new URLSearchParams(search);
    for (const key of URL_PROP_KEYS) {
        params.delete(key);
    }
    // Re-join by hand, keeping `panels=` commas literal like buildSearchString
    // does — URLSearchParams.toString() would percent-encode them. (They'd still
    // round-trip, but the shell rewrites the URL on mount and the two forms
    // should not disagree in between.)
    const remainingParams = [...params.entries()]
        .map(([key, value]) => (
            `${key}=${key === 'panels' ? value : encodeURIComponent(value)}`))
        .join('&');
    return remainingParams ? `${path}?${remainingParams}` : path;
}

// Array.prototype.sort is stable (ES2019+), so equal-order panels keep their
// existing relative order without an explicit index tiebreaker.
export function sortByOrder<T extends { config: { order: number } }>(list: T[]): T[] {
    return [...list].sort((a, b) => a.config.order - b.config.order);
}

// A shared URL prop is carried only while some open panel declares it (see
// declaredUrlProps below). t additionally requires archive playback — -1 (the
// "live" sentinel) and <= 0 are stripped.
export function isUrlPropAllowed(key: string, value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
        return false;
    }
    if (key === 't') {
        const asNumber = Number(value);
        if (!Number.isFinite(asNumber) || asNumber <= 0) {
            return false;
        }
    }
    return true;
}

// The set of URL_PROP_KEYS that at least one currently-open panel declares in
// its `urlSyncedProps`. cameraId is declared by BOTH camera_view and
// camera_settings, so it stays in the URL while *either* is open — closing the
// live view no longer clears the camera the settings form is editing. layoutId
// is shared the same way with the cameras list. t is declared only by
// camera_view, so it still drops when the view closes.
export function getDeclaredUrlProps(panelIds: string[]): Set<string> {
    const keys = new Set<string>();
    for (const id of panelIds) {
        for (const key of getPanelEntry(id)?.config.urlSyncedProps ?? []) {
            keys.add(key);
        }
    }
    return keys;
}

// Serialize directly to a query string (without the leading '?') instead of
// going through URLSearchParams.toString(), which percent-encodes commas to
// %2C. Panel IDs are restricted to [a-z_]; the comma is just a separator and
// is safe unencoded in practice (browsers + URLSearchParams parse it back).
// Other values (camera UUIDs, timestamps) still get encodeURIComponent so any
// unusual character is escaped correctly.
export function buildSearchString(
    panelIds: string[], sharedProps: SharedPanelProps,
): string {
    const parts = [`panels=${panelIds.join(',')}`];
    const declaredKeys = getDeclaredUrlProps(panelIds);
    for (const key of URL_PROP_KEYS) {
        const value = sharedProps[key];
        if (declaredKeys.has(key) && isUrlPropAllowed(key, value)) {
            parts.push(`${key}=${encodeURIComponent(String(value))}`);
        }
    }
    return parts.join('&');
}

/** An org switch makes entity ids from earlier history entries stale. */
export function isForeignOrgEntry(state: unknown): boolean {
    return isPlainObject(state) && Object.hasOwn(state, 'orgId') &&
        state.orgId !== authSession.getActiveOrg();
}

// The URL is authoritative for the rendered panel list, so an interactive
// panel write renders in flushSync, not in a transition.
// Run the flush-synchronous navigation in the next microtask: it still renders
// before the browser's next paint, but is outside the current React lifecycle
// (commitUrl is also reachable from effects, where inline flushSync warns).
// PanelStage still owns its separate post-commit presence/content lifecycle.
//
// `target` may be a function, built inside the microtask: a commit that only
// means to change a shared prop reads the panel list as the commits ahead of
// it left the URL, instead of the snapshot its render captured.
//
// The first write of a person's gesture adds a history entry, so Back undoes it; every other
// write replaces the entry it is on (see navigationGestures.ts). A move of the playhead alone
// replaces too: each seek its own entry would bury the panels under them. A caller that asks
// for `shouldReplace` gets no entry either.
//
// `writeShellRecord` writes the part of the same intent that the URL does not hold. It runs in the
// same flush as the navigation, so the panels get the URL and that part in one render.
export function navigatePanelUrl(
    navigate: (to: PanelTarget, opts: NavigateOptions) => void,
    target: PanelTarget | (() => PanelTarget),
    shouldReplace = false,
    writeShellRecord?: () => void,
): void {
    const gesture = navigationGestures.getCurrent();
    queueMicrotask(() => {
        const to = typeof target === 'function' ? target() : target;
        const here = typeof window === 'undefined' ? null : window.location;
        // A navigation to the URL the document is already at is a no-op
        // by intent but not by cost: it re-renders the router, mints a fresh
        // searchParams identity, and refires every [searchParams]-keyed effect.
        // Left unguarded, a commit scheduled from a settle timer could re-commit
        // the same URL each lap and grow the panel-state update queue until one
        // render pinned the tab for minutes. Checked at flush time (inside the
        // microtask) so back-to-back commits in one task compare against the URL
        // as it actually is when this navigation would run.
        if (here && isCurrentLocation(to, here)) {
            // It renders now, as a navigation would: the agent reads the view state one microtask
            // later.
            if (writeShellRecord) {
                flushSync(writeShellRecord);
            }
            return;
        }
        const shouldPush = !shouldReplace && !!here && !!gesture && !gesture.isPushed &&
            !differsOnlyInMoment(to, here);
        if (gesture && shouldPush) {
            gesture.isPushed = true;
        }
        // One flush for the record and the URL. The shell reads the URL from the store, and the
        // router bridge writes the store in the navigation, so the two render at the end of the
        // flush.
        flushSync(() => {
            writeShellRecord?.();
            navigate(to, {
                replace: !shouldPush,
                state: { orgId: authSession.getActiveOrg() },
            });
        });
    });
}

/** True when target and location differ in nothing but the playhead, `t`. */
export function differsOnlyInMoment(
    target: PanelTarget, loc: { pathname: string; search?: string },
): boolean {
    const omitMoment = (search: string | undefined): string => {
        const params = new URLSearchParams(search);
        params.delete('t');
        return params.toString();
    };
    return loc.pathname === target.pathname &&
        omitMoment(loc.search) === omitMoment(target.search);
}

/**
 * True when target names the location the document is already at. Hash is
 * deliberately ignored: panel URLs never carry a fragment, and a target
 * differing only by hash should still be treated as "already here".
 */
export function isCurrentLocation(
    target: PanelTarget, loc: { pathname: string; search?: string },
): boolean {
    return loc.pathname === target.pathname && (loc.search || '') === (target.search || '');
}

export function readSharedFromUrl(searchParams: URLSearchParams): SharedPanelProps {
    const outSharedPanelProps: SharedPanelProps = {};
    for (const key of URL_PROP_KEYS) {
        const value = searchParams.get(key);
        if (value !== null) {
            outSharedPanelProps[key] = value;
        }
    }
    return outSharedPanelProps;
}

// Parse the `panels=a,b,c` value into a clean id list. null/absent → [].
// Anything the URL offered that names no panel is dropped here, which is what
// makes the rest of the shell's ids trustworthy.
export function parsePanelIds(rawText: string | null): PanelId[] {
    return rawText === null
        ? []
        : rawText.split(',').map((id) => id.trim()).filter(isPanelId);
}

export function isSameIdsArray(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((id, i) => id === b[i]);
}

// Adapt the persisted id list + local props map into the hydrated entries
// resolveOpen / allocator expect ([{id, props, config}]). Drops panels
// missing from the registry (delegated to hydrate()).
export function hydrateOpen(openIds: PanelId[], localProps: PanelLocalProps): OpenPanel[] {
    const mergedPanels = openIds.map((id) => ({ id, props: localProps[id] ?? {} }));
    return hydrate(mergedPanels, registry);
}
